import { useMemo } from 'react';
import {
  useGetAllAchievementsQuery,
  useCheckAchievementQuery,
} from '../../slices/achievementApiSlice';

// icon keys understood by AchievementGrid (see ICONS there); anything else falls back to the trophy
const ICON_ALIASES = {
  trophy: 'trophy', fire: 'fire', money: 'money', chart: 'chart', aim: 'aim', shield: 'shield', medal: 'medal',
  'cash-multiple': 'money', 'chart-line': 'chart', 'shield-check': 'shield',
};
const iconKey = (icon) => ICON_ALIASES[String(icon || '').toLowerCase()] || 'trophy';

/**
 * Achievements come from the backend:
 *  - get-all-achivement  -> every achievement (title, description, target, rewardPoints, icon ...)
 *  - check-achivement    -> the ones this user has unlocked (also saves new ones), with progress
 */
export default function useAchievements() {
  const { data: allRes, isLoading: loadingAll } = useGetAllAchievementsQuery();
  const { data: checkRes, isLoading: loadingCheck } = useCheckAchievementQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const achievements = useMemo(() => {
    const unlocked = new Map((checkRes?.achievements || []).map((a) => [String(a.id), a]));
    const all = Array.isArray(allRes?.data) ? allRes.data : [];

    return all
      .filter((a) => a.isActive !== false)
      .map((a) => {
        const got = unlocked.get(String(a.id));
        return {
          id: a.id,
          title: a.title,
          description: a.description,
          icon: iconKey(a.icon),
          reward: Number(a.rewardPoints) || 0,
          target: Number(a.target) || 0,
          unlocked: !!got,
          progress: got ? Number(got.progress) || 0 : 0,
        };
      });
  }, [allRes, checkRes]);

  const completed = achievements.filter((a) => a.unlocked).length;
  const points = achievements.reduce((sum, a) => sum + (a.unlocked ? a.reward : 0), 0);

  return {
    achievements,
    completed,
    total: achievements.length,
    points,
    loading: loadingAll || loadingCheck,
  };
}